import { useId, useState, type RefObject } from "react";
import { Pill, Tooltip, useFloatingPortal, Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import type { AnnualScheduleGroup } from "../../adapters";
import styles from "./leagueTeachers.module.scss";

/** Shows the first league instructor and a pill exposing the full assignment list. */
export default function LeagueTeachers({ group }: { group: AnnualScheduleGroup }) {
  const { t } = useTranslation();
  const tooltipId = useId();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const extraCount = group.teachers.length - 1;
  const isOpen = extraCount > 0 && (hovered || focused) && !dismissed;
  const { referenceRef, Portal } = useFloatingPortal({ isOpen, offset: [0, -8] });

  return (
    <div className={styles.leagueTeachers}>
      <span
        className={`${styles.leagueTeachers_name} ${group.teachers.length ? "" : styles.leagueTeachers_name__unassigned}`}
      >
        {group.teachers.length
          ? group.teachers[0].instructor.name || group.teachers[0].instructor.code
          : t("dimension.common.without-assignation")}
      </span>
      {extraCount > 0 && (
        <Pill className={styles.leagueTeachers_count}>
          <button
            ref={referenceRef as RefObject<HTMLButtonElement>}
            type="button"
            className={styles.leagueTeachers_trigger}
            aria-label={t("dimension.package.annual.additional-teachers", { count: extraCount })}
            aria-describedby={isOpen ? tooltipId : undefined}
            onMouseEnter={() => {
              setHovered(true);
              setDismissed(false);
            }}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => {
              setFocused(true);
              setDismissed(false);
            }}
            onBlur={() => setFocused(false)}
            onClick={() => setDismissed(false)}
            onKeyDown={event => {
              if (event.key === "Escape") setDismissed(true);
            }}
          >
            + {extraCount}
          </button>
        </Pill>
      )}
      <Portal>
        <Tooltip
          visible
          placement="top"
          label={
            <div id={tooltipId} role="tooltip" className={styles.leagueTeachers_tooltip}>
              <Text weight="medium">{t("dimension.package.annual.assigned-teachers")}</Text>
              <ul className={styles.leagueTeachers_list}>
                {group.teachers.map(({ instructor, isPrimary }) => (
                  <li key={instructor.id}>
                    {instructor.name || instructor.code}
                    {isPrimary && " (" + t("dimension.package.annual.primary") + ")"}
                  </li>
                ))}
              </ul>
            </div>
          }
        >
          <span className={styles.leagueTeachers_tooltipAnchor} />
        </Tooltip>
      </Portal>
    </div>
  );
}
